import { type Node, type Edge } from '@xyflow/react';

export enum WorkflowNodeType {
  Trigger = 'trigger',
  Action = 'action',
  Routing = 'routing',
  PaymentLink = 'payment_link',
  SupplierPayout = 'supplier_payout',
}

// How a payment routing node disburses its staged pool to ranked suppliers.
export enum PaymentRoutingPolicy {
  // Pay the highest-ranked supplier as soon as the pool covers their amount.
  ReleaseOnArrival = 'release_on_arrival',
  // Hold until every linked payment link is fulfilled, then distribute by rank.
  WaitForAll = 'wait_for_all',
}

export enum TriggerType {
  DemandCreated = 'demand_created',
  BidReceived = 'bid_received',
  OrderFulfilled = 'order_fulfilled',
  RiskAlertRaised = 'risk_alert_raised',
  CommitmentStatusChanged = 'commitment_status_changed',
  DeliveryDeadlineApproaching = 'delivery_deadline_approaching',
  CommitmentWithdrawn = 'commitment_withdrawn',
}

export enum ActionType {
  SendNotification = 'send_notification',
  EvaluateRisk = 'evaluate_risk',
  UpdateCompliance = 'update_compliance',
  EscalateToReview = 'escalate_to_review',
  CreateNeed = 'create_need',
  NotifyPartner = 'notify_partner',
}

export enum NodeStatus {
  Valid = 'valid',
  Warning = 'warning',
  Error = 'error',
}

export type RoutingPath = {
  id: string;
  label: string;
  description: string;
  // For payment routing: the supplier this ranked path pays, and how much.
  supplierId?: string;
  amountUsdc?: number;
};

export type TriggerNodeData = {
  nodeType: WorkflowNodeType.Trigger;
  triggerType: TriggerType;
  label: string;
  description: string;
  status: NodeStatus;
};

export type ActionNodeData = {
  nodeType: WorkflowNodeType.Action;
  actionType: ActionType;
  label: string;
  description: string;
  status: NodeStatus;
  config: Record<string, unknown>;
};

export type RoutingNodeData = {
  nodeType: WorkflowNodeType.Routing;
  label: string;
  description: string;
  status: NodeStatus;
  paths: RoutingPath[];
  inputs: string[];
  // Present when this is a payment routing node: the disbursement policy and the
  // amount staged so far across all linked payment links.
  policy?: PaymentRoutingPolicy;
  stagedUsdc?: number;
};

// A payment link feeding a routing node's staging pool. Carries the full
// "collect from client" config so the link is built in the node.
export type PaymentLinkNodeData = {
  nodeType: WorkflowNodeType.PaymentLink;
  label: string; // the link title
  description: string;
  status: NodeStatus;
  linkId?: string;
  clientName?: string;
  targetAmount?: number;
  currency?: string;
  allowPartial?: boolean;
};

// A supplier payout branch (a ranked routing target).
export type SupplierPayoutNodeData = {
  nodeType: WorkflowNodeType.SupplierPayout;
  label: string;
  description: string;
  status: NodeStatus;
  supplierId?: string;
  amountUsdc?: number;
  network?: string;
  walletAddress?: string;
  memo?: string;
};

export type WorkflowNodeData =
  | TriggerNodeData
  | ActionNodeData
  | RoutingNodeData
  | PaymentLinkNodeData
  | SupplierPayoutNodeData;
export type WorkflowNode = Node<WorkflowNodeData>;
export type WorkflowEdge = Edge;

export enum PickerMode {
  Add = 'add',
  Replace = 'replace',
  InsertOnEdge = 'insert_on_edge',
}

export type PickerState = {
  open: boolean;
  mode: PickerMode;
  anchorNodeId?: string;
  anchorEdgeId?: string;
  position?: { x: number; y: number };
};

export type CatalogItem = {
  id: string;
  nodeType: WorkflowNodeType;
  triggerType?: TriggerType;
  actionType?: ActionType;
  label: string;
  description: string;
  icon: React.ComponentType<{ size?: string | number }>;
};
